/* Royal Fans — landing page behaviours (FAQ accordion, finders, comparisons, carousel). Vanilla, no dependencies. */
(function () {
  if (window.__rfLandingLoaded) return;
  window.__rfLandingLoaded = true;

  /* FAQ accordion: <rf-lp-accordion> wrapping buttons[aria-controls] */
  if (!customElements.get('rf-lp-accordion')) {
    customElements.define('rf-lp-accordion', class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-rf-lp-toggle]');
          if (!btn || !this.contains(btn)) return;
          const panel = document.getElementById(btn.getAttribute('aria-controls'));
          if (!panel) return;
          const open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.hidden = open;
        });
      }
    });
  }

  /* Comparison: reveal rows beyond the initial count */
  if (!customElements.get('rf-lp-compare')) {
    customElements.define('rf-lp-compare', class extends HTMLElement {
      connectedCallback() {
        const btn = this.querySelector('[data-rf-lp-more]');
        if (!btn) return;
        btn.addEventListener('click', () => {
          const open = btn.getAttribute('aria-expanded') === 'true';
          this.querySelectorAll('[data-rf-lp-extra]').forEach((row) => { row.hidden = open; });
          btn.setAttribute('aria-expanded', open ? 'false' : 'true');
          btn.textContent = open ? btn.dataset.labelMore : btn.dataset.labelLess;
        });
      }
    });
  }

  /* Fan finder: choose the rule with the most exact matches and no conflicting answers */
  if (!customElements.get('rf-lp-finder')) {
    customElements.define('rf-lp-finder', class extends HTMLElement {
      connectedCallback() {
        const form = this.querySelector('form');
        const dataEl = this.querySelector('script[type="application/json"]');
        if (!form || !dataEl) return;
        let config;
        try { config = JSON.parse(dataEl.textContent); } catch (err) { return; }
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const answers = {
            room: form.elements.room.value,
            power: form.elements.power.value,
            style: form.elements.style.value
          };
          let best = null;
          let bestScore = -1;
          (config.rules || []).forEach((rule) => {
            let score = 0;
            for (const key of ['room', 'power', 'style']) {
              if (!rule[key]) continue;
              if (!answers[key]) continue;
              if (rule[key].toLowerCase() !== answers[key].toLowerCase()) return;
              score += 1;
            }
            if (score > bestScore && rule.url) { best = rule; bestScore = score; }
          });
          const target = best && bestScore > 0 ? best.url : config.fallback;
          if (target) window.location.href = target;
        });
      }
    });
  }

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Carousel: prev/next buttons scroll the track; buttons hide at either end */
  if (!customElements.get('rf-lp-carousel')) {
    customElements.define('rf-lp-carousel', class extends HTMLElement {
      connectedCallback() {
        const track = this.querySelector('[data-rf-lp-track]');
        if (!track) return;
        const prev = this.querySelector('[data-rf-lp-prev]');
        const next = this.querySelector('[data-rf-lp-next]');
        const sync = () => {
          const max = track.scrollWidth - track.clientWidth - 2;
          if (prev) prev.disabled = track.scrollLeft <= 2;
          if (next) next.disabled = track.scrollLeft >= max;
        };
        const go = (dir) => track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: reduceMotion() ? 'auto' : 'smooth' });
        if (prev) prev.addEventListener('click', () => go(-1));
        if (next) next.addEventListener('click', () => go(1));
        track.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync);
        /* Optional "view all" toggle that turns the carousel into a grid */
        const expand = this.querySelector('[data-rf-lp-expand]');
        if (expand) {
          const label = expand.querySelector('[data-rf-lp-label]') || expand;
          expand.addEventListener('click', () => {
            const open = expand.getAttribute('aria-expanded') === 'true';
            expand.setAttribute('aria-expanded', open ? 'false' : 'true');
            label.textContent = open ? expand.dataset.labelMore : expand.dataset.labelLess;
            this.classList.toggle('is-grid', !open);
            track.scrollLeft = 0;
            if (open) this.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
            sync();
          });
        }
        sync();
      }
    });
  }

  /* Product finder: shows the best-matching product cards inline instead of leaving the page */
  if (!customElements.get('rf-lp-pfinder')) {
    customElements.define('rf-lp-pfinder', class extends HTMLElement {
      connectedCallback() {
        const form = this.querySelector('form');
        const dataEl = this.querySelector('script[type="application/json"]');
        const results = this.querySelector('[data-rf-lp-results]');
        if (!form || !dataEl || !results) return;
        let config;
        try { config = JSON.parse(dataEl.textContent); } catch (err) { return; }
        const items = Array.from(results.querySelectorAll('[data-rule]'));
        const empty = results.querySelector('[data-rf-lp-empty]');
        const title = results.querySelector('[data-rf-lp-rtitle]');
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const answers = ['q1', 'q2', 'q3'].map((k) => (form.elements[k] ? form.elements[k].value : ''));
          let bestScore = 0;
          const scores = (config.rules || []).map((rule) => {
            let score = 0;
            for (let i = 0; i < 3; i++) {
              const want = (rule.q[i] || '').toLowerCase();
              const got = (answers[i] || '').toLowerCase();
              if (!want || !got) continue;
              if (want !== got) return -1;
              score += 1;
            }
            if (score > bestScore) bestScore = score;
            return score;
          });
          const seen = new Set();
          let shown = 0;
          items.forEach((item) => {
            const idx = Number(item.dataset.rule);
            const pid = item.dataset.pid;
            const match = bestScore > 0 && scores[idx] === bestScore && !seen.has(pid) && shown < (config.max || 3);
            if (match) { seen.add(pid); shown += 1; }
            item.hidden = !match;
          });
          if (empty) empty.hidden = shown > 0;
          if (title) title.hidden = shown === 0;
          results.hidden = false;
          const focusEl = shown > 0 ? title : empty;
          if (focusEl) focusEl.focus({ preventScroll: true });
          results.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'nearest' });
        });
      }
    });
  }

  /* Multi-model comparison: on small screens show two chosen models side by side */
  if (!customElements.get('rf-lp-ctable')) {
    customElements.define('rf-lp-ctable', class extends HTMLElement {
      connectedCallback() {
        const picks = Array.from(this.querySelectorAll('[data-rf-lp-pick]'));
        if (picks.length < 2) return;
        const cells = Array.from(this.querySelectorAll('[data-col]'));
        const mq = window.matchMedia('(max-width: 749px)');
        const apply = () => {
          const keep = picks.map((s) => s.value);
          cells.forEach((cell) => { cell.hidden = mq.matches && keep.indexOf(cell.dataset.col) === -1; });
          this.toggleAttribute('data-picked', mq.matches);
        };
        picks.forEach((s, i) => s.addEventListener('change', () => {
          const other = picks[1 - i];
          if (other.value === s.value) {
            const alt = Array.from(other.options).find((o) => o.value !== s.value);
            if (alt) other.value = alt.value;
          }
          apply();
        }));
        if (mq.addEventListener) mq.addEventListener('change', apply); else mq.addListener(apply);
        apply();
      }
    });
  }
})();
